import React, { useRef, useEffect, useState, useCallback } from "react";
import "./ThemedTable.css";
import RenderingProviderSelection from "./RenderingProviderSelection";
import FacilitySelection from "./FacilitySelection";
import DepartmentSelection from "./DepartmentSelection";
import callAutomationService from "../../services/callAutomationService";
import { formatDateInput, isDateField } from "../../utils/helpers/format-date-input";

// Add a function to detect error values, including new error types
function isErrorValue(value) {
  return (
    (typeof value === "string" && value.startsWith("Missing Value")) ||
    (typeof value === "string" && value.startsWith("Invalid"))
  );
}

export function countErrorCells(data, columns) {
  let count = 0;
  data.forEach((row) => {
    columns.forEach((col) => {
      const value = row[col.name];
      if (isErrorValue(value)) count++;
    });
  });
  return count;
}

// Function to determine the field type based on column name
function getFieldType(columnName) {
  const name = columnName.toLowerCase();
  
  // Group/Facility NPI dropdown (comes first)
  if ((name.includes('group') && name.includes('npi')) || 
      (name.includes('facility') && name.includes('npi'))) {
    return 'facility';
  }
  
  // Department Name dropdown (comes after facility, before rendering provider)
  if (name.includes('department') && name.includes('name')) {
    return 'department';
  }
  
  // Rendering Provider NPI dropdown (comes after department)
  if (name.includes('rendering provider npi')) {
    return 'renderingProvider';
  }
  
  // Treatment Address field (auto-populated from facility selection)
  if (name.includes('treatment') && name.includes('address')) {
    return 'treatmentAddress';
  }
  
  // Member Street address: enable Google Places for member street specifically
  if (name.includes('member street')) {
    return 'memberAddress';
  }

  // Default to text input
  return 'text';
}

// ThemedTable: A custom, theme-aware table component
function ThemedTable({
  columns,
  data,
  containerHeight = "auto",
  bodyHeight = "45vh",
  themeMode,
  currentTheme,
  onCellEdit,
  editingCell,
  setEditingCell,
  itemsPerPageOptions = [5, 10, 20],
  defaultItemsPerPage = 10,
  // Enable checkbox column for multi-select
  enableCheckboxSelection = false,
  // Single-row selection index (legacy / primary selection)
  selectedRowIdx = null,
  // Multiple row selection support via row IDs
  selectedRowIds = [],
  // Called when a row is clicked; receives (absoluteIndex, rowId)
  onRowSelect = () => {},
  // Called when the header "select all" checkbox is toggled; receives (selectAll: boolean)
  onSelectAll = null,
  targetPage = null,
  onTargetPageReached = () => {},
}) {
  // Fallbacks in case theme is missing
  const isLight = themeMode === "light";
  const tableBg = isLight
    ? currentTheme?.callAutomationPage?.mainBackground || "#fff"
    : currentTheme?.callAutomationPage?.mainBackground || "#231f3a";
  const headerBg = isLight
    ? currentTheme?.callAutomationPage?.tableHeaderBg || "#f5f5f7"
    : currentTheme?.callAutomationPage?.tableHeaderBg || "#2d284d";
  const headerText = isLight ? "#000" : "#fff";
  const borderColor = isLight
    ? currentTheme?.callAutomationPage?.tableBorder || "#e0e0e0"
    : currentTheme?.callAutomationPage?.tableBorder || "#2d284d";
  const rowEven = isLight
    ? currentTheme?.callAutomationPage?.tableRowEven || "#fff"
    : currentTheme?.callAutomationPage?.tableRowEven || "#28244a";
  const rowOdd = isLight
    ? currentTheme?.callAutomationPage?.tableRowOdd || "#f7f7fa"
    : currentTheme?.callAutomationPage?.tableRowOdd || "#231f3a";
  const textColor = isLight
    ? currentTheme?.callAutomationPage?.tableText || "#222"
    : currentTheme?.callAutomationPage?.tableText || "#f8f8ff";
  const highlight = isLight
    ? currentTheme?.callAutomationPage?.highlight || "#ff4e3a"
    : currentTheme?.callAutomationPage?.highlight || "#ff4e3a";
  const highlightBg = "#2F2D4C"

  const containerRef = useRef(null);
  const [internalEditingCell, setInternalEditingCell] = useState({
    rowIdx: null,
    colName: null,
  });
  const [editValue, setEditValue] = useState("");

  // Use controlled editingCell if provided, otherwise use internal
  const activeEditingCell = editingCell || internalEditingCell;
  const setActiveEditingCell = setEditingCell || setInternalEditingCell;

  // Track current row data for facility-provider filtering
  const [currentRowData, setCurrentRowData] = useState({});

  const handleEditStart = (rowIdx, colName, value) => {
    setEditValue(value);
    setActiveEditingCell({ rowIdx, colName });
  };

  const handleEditChange = (e) => {
    setEditValue(e.target.value);
  };

  const handleEditBlur = (rowIdx, colName) => {
    if (onCellEdit) {
      // Apply automatic date formatting for date fields
      const formattedValue = isDateField(colName) ? formatDateInput(editValue, colName) : editValue;
      onCellEdit(rowIdx, colName, formattedValue);
    }
    setActiveEditingCell({ rowIdx: null, colName: null });
  };

  // Helper function to format treatment address from facility location
  const formatTreatmentAddress = (location) => {
    if (!location) return '';
    
    const { streetAddress = '', city = '', state = '', zipcode = '' } = location;
    return `${streetAddress} ${city} ${state} ${zipcode}`.trim();
  };

  // Display-friendly address (with commas), built from selected facility object
  const formatDisplayAddress = (location) => {
    if (!location) return '';
    const parts = [];
    if (location.streetAddress) parts.push(location.streetAddress);
    if (location.city) parts.push(location.city);
    // Join street + city with commas, then add state+zip
    const left = parts.join(', ');
    const right = [location.state || '', location.zipcode || ''].filter(Boolean).join(' ');
    return right ? `${left}${left ? ', ' : ''}${right}` : left;
  };

  // Normalize addresses to improve matching (strip commas/extra spaces, lowercase)
  const normalizeAddress = (address) => {
    if (!address || typeof address !== 'string') return '';
    return address
      .replace(/,/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  };

  // Helper function to find the correct facility based on NPI and treatment address
  const findCorrectFacility = useCallback(async (npi, treatmentAddress) => {
    if (!npi) return null;
    
    try {
      const facilities = await callAutomationService.getFacilities();
      const facilitiesWithNPI = facilities.filter(facility => facility.value === npi);
      
      if (facilitiesWithNPI.length === 1) {
        return facilitiesWithNPI[0];
      } else if (facilitiesWithNPI.length > 1 && treatmentAddress) {
        // Multiple facilities with same NPI - match by treatment address
        const matchingFacility = facilitiesWithNPI.find(facility => {
          const facilityAddress = formatTreatmentAddress(facility.location);
          return facilityAddress === treatmentAddress.trim();
        });
        
        if (matchingFacility) {
          console.log(`🎯 Found correct facility: ${matchingFacility.facilityName} for address: ${treatmentAddress}`);
          return matchingFacility;
        }
      }
      
      // Return first facility as fallback
      return facilitiesWithNPI[0] || null;
    } catch (error) {
      console.error('❌ Error finding correct facility:', error);
      return null;
    }
  }, []);

  // ============================================================================
  // HELPER FUNCTIONS FOR DROPDOWN CASCADING LOGIC (Separation of Concerns)
  // ============================================================================

  /**
   * Fetches all required data for dropdown cascading logic
   * @returns {Promise<{facilities: Array, departments: Array, providers: Array}>}
   */
  const fetchDropdownData = async () => {
    const [facilities, departments, providers] = await Promise.all([
      callAutomationService.getFacilities(),
      callAutomationService.getDepartments(),
      callAutomationService.getRenderingProviders()
    ]);
    return { facilities, departments, providers };
  };

  /**
   * Gets the effective facility IDs for filtering
   * @param {Array} facilities - All facilities
   * @param {string} facilityNPI - Selected facility NPI
   * @param {string|null} specificFacilityId - Specific facility ID if known
   * @returns {Array<string>} Array of facility IDs
   */
  const getEffectiveFacilityIds = (facilities, facilityNPI, specificFacilityId) => {
    if (specificFacilityId) return [specificFacilityId];
    return facilities.filter(f => f.value === facilityNPI).map(f => f.id);
  };

  /**
   * Gets available departments for given facility IDs
   * @param {Array} allDepartments - All departments
   * @param {Array<string>} facilityIds - Facility IDs to filter by
   * @returns {Array} Filtered departments
   */
  const getAvailableDepartments = (allDepartments, facilityIds) => {
    return allDepartments.filter(d => facilityIds.includes(d.facilityId));
  };

  /**
   * Determines the department value after a facility change
   * @param {Array} availableDepartments - Departments available for new facility
   * @param {string} currentDepartmentName - Currently selected department name (unused, kept for API compatibility)
   * @returns {{value: string, action: 'auto-select'|'clear'}}
   */
  const determineDepartmentValue = (availableDepartments, currentDepartmentName) => {
    console.log('🔍 determineDepartmentValue:', {
      availableCount: availableDepartments.length,
      availableNames: availableDepartments.map(d => d.name),
      currentDepartmentName: currentDepartmentName || '(empty)'
    });
    
    // ONLY auto-select if exactly one department available
    if (availableDepartments.length === 1) {
      console.log('✅ Action: auto-select ->', availableDepartments[0].name);
      return { value: availableDepartments[0].name, action: 'auto-select' };
    }
    
    // Otherwise ALWAYS clear (whether 0 or multiple departments)
    console.log('✅ Action: clear (multiple or no departments)');
    return { value: '', action: 'clear' };
  };

  /**
   * Gets available providers for given facility and department IDs
   * @param {Array} allProviders - All providers
   * @param {Array<string>} facilityIds - Facility IDs to filter by
   * @param {Array<string>} departmentIds - Department IDs to filter by (empty = facility-only filter)
   * @returns {Array} Filtered providers
   */
  const getAvailableProviders = (allProviders, facilityIds, departmentIds) => {
    if (departmentIds.length > 0) {
      // Filter by both facility and department
      return allProviders.filter(p =>
        p.affiliations?.some(aff =>
          facilityIds.includes(aff.facilityId) && departmentIds.includes(aff.departmentId)
        )
      );
    }
    // Filter by facility only
    return allProviders.filter(p =>
      p.affiliations?.some(aff => facilityIds.includes(aff.facilityId))
    );
  };

  /**
   * Determines the provider value after a facility/department change
   * @param {Array} availableProviders - Providers available for current facility/department
   * @param {string} currentProviderNPI - Currently selected provider NPI (unused, kept for API compatibility)
   * @returns {{value: string, action: 'auto-select'|'clear'}}
   */
  const determineProviderValue = (availableProviders, currentProviderNPI) => {
    console.log('🔍 determineProviderValue:', {
      availableCount: availableProviders.length,
      availableProviders: availableProviders.map(p => ({ name: p.fullName, npi: p.npi })),
      currentProviderNPI: currentProviderNPI || '(empty)'
    });
    
    // ONLY auto-select if exactly one provider available
    if (availableProviders.length === 1) {
      console.log('✅ Action: auto-select ->', availableProviders[0].fullName);
      return { value: availableProviders[0].npi, action: 'auto-select', provider: availableProviders[0] };
    }
    
    // Otherwise ALWAYS clear (whether 0 or multiple providers)
    console.log('✅ Action: clear (multiple or no providers)');
    return { value: '', action: 'clear' };
  };

  /**
   * Gets department IDs for a given department name within specific facilities
   * @param {Array} allDepartments - All departments
   * @param {string} departmentName - Department name to find
   * @param {Array<string>} facilityIds - Facility IDs to filter by
   * @returns {Array<string>} Department IDs
   */
  const getDepartmentIds = (allDepartments, departmentName, facilityIds) => {
    if (!departmentName) return [];
    return allDepartments
      .filter(d => d.name === departmentName && facilityIds.includes(d.facilityId))
      .map(d => d.id);
  };

  /**
   * Gets treatment address from facility
   * @param {Array} facilities - All facilities
   * @param {string} facilityId - Specific facility ID
   * @param {string} facilityNPI - Facility NPI (fallback)
   * @returns {string|null} Treatment address or null
   */
  const getTreatmentAddress = (facilities, facilityId, facilityNPI) => {
    let facility = null;
    
    if (facilityId) {
      facility = facilities.find(f => f.id === facilityId);
    } else if (facilityNPI) {
      const matchingFacilities = facilities.filter(f => f.value === facilityNPI);
      if (matchingFacilities.length === 1) {
        facility = matchingFacilities[0];
      }
    }
    
    if (facility?.location) {
      return formatDisplayAddress(facility.location);
    }
    return null;
  };

  /**
   * Gets current row field values
   * @param {number} rowIdx - Row index
   * @param {Object} columnMap - Map of field types to column names
   * @returns {Object} Current values for each field
   */
  const getCurrentRowValues = (rowIdx, columnMap) => {
    const values = {};
    Object.entries(columnMap).forEach(([fieldType, colName]) => {
      if (colName) {
        values[fieldType] = (currentRowData[rowIdx]?.[colName] || data[rowIdx]?.[colName] || '').trim();
      }
    });
    return values;
  };

  // ============================================================================
  // MAIN DROPDOWN CHANGE HANDLER
  // ============================================================================

  /**
   * Handle dropdown value changes with cascading updates
   * Implements auto-selection logic for Facility → Department → Provider chain
   */
  const handleDropdownChange = async (rowIdx, colName, value, options = {}) => {
    const fieldType = getFieldType(colName);
    const fieldsToUpdate = { [colName]: value };

    // Get column references
    const columnMap = {
      facility: columns.find(col => getFieldType(col.name) === 'facility')?.name,
      department: columns.find(col => getFieldType(col.name) === 'department')?.name,
      provider: columns.find(col => getFieldType(col.name) === 'renderingProvider')?.name,
      treatmentAddress: columns.find(col => getFieldType(col.name) === 'treatmentAddress')?.name
    };

    try {
      // Fetch all data once
      const { facilities, departments, providers } = await fetchDropdownData();

      if (fieldType === 'facility') {
        // === FACILITY CHANGE ===
        // Use newFacilityId from options if provided (passed from composite value selection)
        // This is critical because React state updates are async, so data[rowIdx].facilityId 
        // would still have the OLD value at this point
        const facilityId = options.newFacilityId || data[rowIdx]?.facilityId || currentRowData[rowIdx]?.facilityId;
        const facilityIds = getEffectiveFacilityIds(facilities, value, options.newFacilityId ? facilityId : null);
        const currentValues = getCurrentRowValues(rowIdx, columnMap);

        console.log('🏥 FACILITY CHANGE:', {
          newFacilityNPI: value,
          newFacilityIdFromOptions: options.newFacilityId || '(not provided)',
          facilityId,
          facilityIds,
          currentValues,
          columnMap
        });

        // Step 1: Determine department
        const availableDepts = getAvailableDepartments(departments, facilityIds);
        const deptResult = determineDepartmentValue(availableDepts, currentValues.department);
        
        console.log('📋 Department result:', deptResult);
        
        // Always update department field
        if (columnMap.department) {
          fieldsToUpdate[columnMap.department] = deptResult.value;
          console.log('📝 Adding to fieldsToUpdate:', columnMap.department, '=', `"${deptResult.value}"`);
        }

        // Step 2: Determine provider (based on final department)
        const finalDeptName = deptResult.value; // Use the determined value
        const deptIds = getDepartmentIds(departments, finalDeptName, facilityIds);
        const availableProvs = getAvailableProviders(providers, facilityIds, deptIds);
        const provResult = determineProviderValue(availableProvs, currentValues.provider);

        console.log('📋 Provider result:', provResult);

        // Always update provider field
        if (columnMap.provider) {
          fieldsToUpdate[columnMap.provider] = provResult.value;
          console.log('📝 Adding to fieldsToUpdate:', columnMap.provider, '=', `"${provResult.value}"`);
        }

        // Step 3: Treatment address
        if (columnMap.treatmentAddress && !options.skipTreatmentAddress) {
          const address = getTreatmentAddress(facilities, facilityId, value);
          if (address) {
            fieldsToUpdate[columnMap.treatmentAddress] = address;
          }
        }

      } else if (fieldType === 'department') {
        // === DEPARTMENT CHANGE ===
        const currentValues = getCurrentRowValues(rowIdx, columnMap);
        const facilityIds = getEffectiveFacilityIds(facilities, currentValues.facility, data[rowIdx]?.facilityId);
        const deptIds = getDepartmentIds(departments, value, facilityIds);
        const availableProvs = getAvailableProviders(providers, facilityIds, deptIds);
        const provResult = determineProviderValue(availableProvs, currentValues.provider);

        console.log('🏢 DEPARTMENT CHANGE - Provider result:', provResult);

        // Always update provider field
        if (columnMap.provider) {
          fieldsToUpdate[columnMap.provider] = provResult.value;
        }
      }

    } catch (error) {
      console.error('Error in handleDropdownChange:', error);
    }

    console.log('🚀 FINAL fieldsToUpdate:', fieldsToUpdate);

    // Apply all field updates
    Object.entries(fieldsToUpdate).forEach(([fieldName, fieldValue]) => {
      console.log('📤 Calling onCellEdit:', fieldName, '=', fieldValue);
      if (onCellEdit) {
        onCellEdit(rowIdx, fieldName, fieldValue);
      }
    });

    setActiveEditingCell({ rowIdx: null, colName: null });

    // Update current row data for filtering
    const pageRowIdx = rowIdx - indexOfFirstItem;
    if (pageRowIdx >= 0 && pageRowIdx < currentItems.length) {
      setCurrentRowData(prev => ({
        ...prev,
        [rowIdx]: { ...prev[rowIdx], ...fieldsToUpdate }
      }));
    }
  };

  // Get facility ID for current row to filter rendering providers  
  const getFacilityIdForRow = (rowIdx) => {
    const pageRowIdx = rowIdx - indexOfFirstItem;
    if (pageRowIdx >= 0 && pageRowIdx < currentItems.length) {
      const row = currentItems[pageRowIdx];
      
      // Look for facility NPI columns
      const facilityColumn = columns.find(col => 
        getFieldType(col.name) === 'facility'
      );
      
      if (facilityColumn) {
        const facilityNPI = currentRowData[rowIdx]?.[facilityColumn.name] || row[facilityColumn.name];
        return facilityNPI; // Return NPI for now - actual facility matching happens in dropdown components
      }
    }
    return null;
  };

  // Get the correct facility ID by matching NPI and treatment address (internal helper)
  const getCorrectFacilityId = async (rowIdx) => {
    const pageRowIdx = rowIdx - indexOfFirstItem;
    if (pageRowIdx < 0 || pageRowIdx >= currentItems.length) return null;
    const row = currentItems[pageRowIdx];
    const facilityColumn = columns.find(col => getFieldType(col.name) === 'facility');
    const treatmentAddressColumn = columns.find(col => getFieldType(col.name) === 'treatmentAddress');
    if (!facilityColumn) return null;
    const facilityNPI = currentRowData[rowIdx]?.[facilityColumn.name] || row[facilityColumn.name];
    const treatmentAddress = treatmentAddressColumn ? (currentRowData[rowIdx]?.[treatmentAddressColumn.name] || row[treatmentAddressColumn.name] || '') : '';
    if (!facilityNPI) return null;
    const correctFacility = await findCorrectFacility(facilityNPI, treatmentAddress);
    return correctFacility?.id || null;
  };

  // Get rendering provider NPI for current row to filter facilities
  const getRenderingProviderNPIForRow = (rowIdx) => {
    const pageRowIdx = rowIdx - indexOfFirstItem;
    if (pageRowIdx >= 0 && pageRowIdx < currentItems.length) {
      const row = currentItems[pageRowIdx];
      
      // Look for rendering provider NPI columns
      const providerColumn = columns.find(col => 
        getFieldType(col.name) === 'renderingProvider'
      );
      
      if (providerColumn) {
        const providerNPI = currentRowData[rowIdx]?.[providerColumn.name] || row[providerColumn.name];
        return providerNPI; // This is the NPI which we need to match with provider data
      }
    }
    return null;
  };

  // Get department name for current row to filter rendering providers
  const getDepartmentForRow = (rowIdx) => {
    const pageRowIdx = rowIdx - indexOfFirstItem;
    if (pageRowIdx >= 0 && pageRowIdx < currentItems.length) {
      const row = currentItems[pageRowIdx];
      
      // Look for department name columns
      const departmentColumn = columns.find(col => 
        getFieldType(col.name) === 'department'
      );
      
      if (departmentColumn) {
        const departmentName = currentRowData[rowIdx]?.[departmentColumn.name] || row[departmentColumn.name];
        return departmentName;
      }
    }
    return null;
  };

  // Render the appropriate input field based on column type
  const renderInputField = (rowIdx, colName, value, fieldType) => {
    const absIdx = rowIdx + indexOfFirstItem;
    const isSelected = absIdx === selectedRowIdx;

    const commonProps = {
      themeMode,
      currentTheme,
      style: { 
        border: `1px solid ${highlight}`,
        borderRadius: "4px",
        fontSize: "0.98rem",
        width: "90%" // Make dropdowns 10% smaller than their container
      }
    };

    switch (fieldType) {
      case 'facility':
        const renderingProviderNPI = getRenderingProviderNPIForRow(absIdx);
        // derive current treatment address for this row (for disambiguation in FacilitySelection)
        const treatmentAddressColumn = columns.find(col => getFieldType(col.name) === 'treatmentAddress');
        const treatmentAddressValue = treatmentAddressColumn ? (currentRowData[absIdx]?.[treatmentAddressColumn.name] || currentItems[rowIdx]?.[treatmentAddressColumn.name] || '') : '';
        // If we already have a facilityId for this row, pass a composite value so the correct
        // facility remains selected even when multiple facilities share the same NPI
        const facilityIdFromRow = currentItems[rowIdx]?.facilityId || currentRowData[absIdx]?.facilityId || data[absIdx]?.facilityId || '';
        const facilityValueForDropdown = facilityIdFromRow && typeof value === 'string' && !value.includes('::')
          ? `${value}::${facilityIdFromRow}`
          : value;
        return (
          <FacilitySelection
            {...commonProps}
            value={facilityValueForDropdown}
            onChange={async (newValue) => {
              // If composite value `${npi}::${facilityId}`, split and emit both
              if (typeof newValue === 'string' && newValue.includes('::')) {
                const [groupNPI, selectedFacilityId] = newValue.split('::');
                // write NPI to visible cell
                if (onCellEdit) onCellEdit(absIdx, colName, groupNPI);
                // store facilityId locally (not for backend)
                if (onCellEdit) onCellEdit(absIdx, 'facilityId', selectedFacilityId);
                // auto-fill treatment address
                const treatmentAddressCol = columns.find(c => getFieldType(c.name) === 'treatmentAddress');
                if (treatmentAddressCol) {
                  try {
                    const facilities = await callAutomationService.getFacilities();
                    const selectedFacility = facilities.find(f => f.id === selectedFacilityId);
                    if (selectedFacility) {
                      // Use display-friendly formatting consistent with input data
                      const ta = formatDisplayAddress(selectedFacility.location);
                      if (ta) onCellEdit(absIdx, treatmentAddressCol.name, ta);
                    }
                  } catch (_) {}
                }
                // trigger cascading logic using the NPI, pass the NEW facilityId directly
                handleDropdownChange(absIdx, colName, groupNPI, { skipTreatmentAddress: true, newFacilityId: selectedFacilityId });
              } else {
                handleDropdownChange(absIdx, colName, newValue);
              }
            }}
            placeholder="Select Facility"
            renderingProviderNPI={renderingProviderNPI} // Pass rendering provider NPI to filter facilities
            treatmentAddress={treatmentAddressValue}
          />
        );

      case 'department':
        const facilityNPIForDepartment = getFacilityIdForRow(absIdx);
        const facilityIdForDepartment = currentItems[rowIdx]?.facilityId || currentRowData[absIdx]?.facilityId || data[absIdx]?.facilityId || null;
        return (
          <DepartmentSelection
            {...commonProps}
            value={value}
            onChange={(newValue) => handleDropdownChange(absIdx, colName, newValue)}
            placeholder="Select Department"
            facilityId={facilityIdForDepartment} // Pass exact facility ID for filtering
            facilityNPI={facilityNPIForDepartment} // Pass facility NPI as fallback
          />
        );

      case 'renderingProvider':
        const facilityNPI = getFacilityIdForRow(absIdx);
        const facilityIdForProvider = currentItems[rowIdx]?.facilityId || currentRowData[absIdx]?.facilityId || data[absIdx]?.facilityId || null;
        const departmentName = getDepartmentForRow(absIdx);
        return (
          <RenderingProviderSelection
            {...commonProps}
            value={value}
            onChange={(newValue) => handleDropdownChange(absIdx, colName, newValue)}
            placeholder="Select Rendering Provider"
            facilityId={facilityIdForProvider} // Pass exact facility ID for filtering
            facilityNPI={facilityNPI} // Pass facility NPI as fallback
            departmentName={departmentName} // Pass department name for more specific filtering
          />
        );

      case 'treatmentAddress':
        return (
          <input
            type="text"
            value={editValue}
            onChange={handleEditChange}
            onBlur={() => handleEditBlur(absIdx, colName)}
            autoFocus
            placeholder="Auto-filled from facility selection"
            style={{
              width: "95%",
              background: themeMode === 'dark' ? "rgba(76, 175, 80, 0.1)" : "rgba(76, 175, 80, 0.05)",
              color: isSelected ? "#fff" : textColor,
              border: `1px solid ${themeMode === 'dark' ? "rgba(76, 175, 80, 0.3)" : "rgba(76, 175, 80, 0.5)"}`,
              borderRadius: "4px",
              padding: "4px",
              fontSize: "0.98rem"
            }}
            title="This field is auto-populated when you select a facility, but you can edit it manually if needed"
          />
        );

      case 'memberAddress':
        return (
          <input
            type="text"
            value={editValue}
            onChange={handleEditChange}
            onBlur={() => handleEditBlur(absIdx, colName)}
            autoFocus
            placeholder="Member address"
            style={{
              width: "95%",
              background: "transparent",
              color: isSelected ? "#fff" : textColor,
              border: `1px solid ${highlight}`,
              borderRadius: "4px",
              padding: "4px",
              fontSize: "0.98rem"
            }}
          />
        );

      default: // text
        return (
          <input
            type="text"
            value={editValue}
            onChange={handleEditChange}
            onBlur={() => handleEditBlur(absIdx, colName)}
            autoFocus
            style={{
              width: "95%",
              background: "transparent",
              color: isSelected ? "#fff" : textColor,
              border: `1px solid ${highlight}`,
              borderRadius: "4px",
              padding: "4px",
              fontSize: "0.98rem"
            }}
          />
        );
    }
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(defaultItemsPerPage);
  const totalPages = Math.ceil(data.length / rowsPerPage);
  const indexOfLastItem = currentPage * rowsPerPage;
  const indexOfFirstItem = indexOfLastItem - rowsPerPage;
  const currentItems = data.slice(indexOfFirstItem, indexOfLastItem);

  // Load dropdown data proactively when table loads
  useEffect(() => {
    // Import and preload data when component mounts
    import('../../services/callAutomationService').then(service => {
      service.default.getFacilities();
      service.default.getRenderingProviders();
      service.default.getDepartments();
      console.log('📋 Preloading dropdown data for table');
    });
  }, []);

  // Initialize correct facility IDs when data loads
  useEffect(() => {
    const initializeFacilityIds = async () => {
      if (!data || data.length === 0) return;
      
      const facilityColumn = columns.find(col => getFieldType(col.name) === 'facility');
      const treatmentAddressColumn = columns.find(col => getFieldType(col.name) === 'treatmentAddress');
      
      if (facilityColumn && treatmentAddressColumn) {
        let updatedRows = false;
        
        for (let i = 0; i < data.length; i++) {
          const row = data[i];
          const facilityNPI = row[facilityColumn.name];
          const treatmentAddress = row[treatmentAddressColumn.name];
          
          if (facilityNPI && treatmentAddress && !row.facilityId) {
            const correctFacility = await findCorrectFacility(facilityNPI, treatmentAddress);
            if (correctFacility) {
              row.facilityId = correctFacility.id;
              updatedRows = true;
              console.log(`🎯 Initialized facilityId for row ${i}: ${correctFacility.facilityName}`);
            }
          }
        }
        
        if (updatedRows) {
          console.log('📋 Completed facility ID initialization for loaded data');
        }
      }
    };
    
    if (data && data.length > 0) {
      initializeFacilityIds();
    }
  }, [data, columns, findCorrectFacility]);

  // Reset to page 1 if current page is out of bounds or data changes significantly
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // Reset to page 1 when data changes significantly (e.g., after upload)
  useEffect(() => {
    if (data.length > 0 && currentPage > 1) {
      // Only reset if we're not on the first page and data has changed
      const newTotalPages = Math.ceil(data.length / rowsPerPage);
      if (currentPage > newTotalPages) {
        setCurrentPage(1);
      }
    }
  }, [data.length, rowsPerPage]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
  }, []);

  // Navigate to target page when provided
  useEffect(() => {
    if (targetPage && targetPage > 0 && targetPage <= totalPages) {
      console.log("Navigating to target page:", targetPage);
      setCurrentPage(targetPage);
      // Notify parent that target page has been reached
      setTimeout(() => {
        onTargetPageReached();
      }, 100);
    }
  }, [targetPage, totalPages, onTargetPageReached]);

  return (
    <>
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: bodyHeight,
          borderRadius: "12px",
          background: tableBg,
          boxShadow: "0 4px 24px 0 rgba(0,0,0,0.18)",
          border: `1px solid ${borderColor}`,
          overflow: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: isLight ? "#504e67 #c2c2c2" : "#cac9d4 #312e4d",
        }}
        className={isLight ? "light-scrollbar" : "dark-scrollbar"}
      >
        <style>{`
          .light-scrollbar::-webkit-scrollbar { width: 8px; background: #e0e0e0; }
          .light-scrollbar::-webkit-scrollbar-thumb { background: #bdbdbd; border-radius: 16px; }
          .light-scrollbar::-webkit-scrollbar-track { background: #f5f5f7; border-radius: 16px; }
          .light-scrollbar::-webkit-scrollbar-button { display: none; height: 0; width: 0; }
          .dark-scrollbar::-webkit-scrollbar { width: 8px; background: #312e4d; }
          .dark-scrollbar::-webkit-scrollbar-thumb { background: #504e67; border-radius: 16px; }
          .dark-scrollbar::-webkit-scrollbar-track { background: #23213a; border-radius: 16px; }
          .dark-scrollbar::-webkit-scrollbar-button { display: none; height: 0; width: 0; }
          .selected-row { background: #ff4e3a !important; color: #fff !important; }
          .selected-row { background: #ff4e3a !important; color: #fff !important; }
        `}</style>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontFamily: "Inter, sans-serif",
          }}
        >
          <thead>
            <tr>
              {enableCheckboxSelection && (
                <th
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    padding: "14px 10px",
                    background: headerBg,
                    borderBottom: `3px solid ${highlight}`,
                    width: "3rem",
                    textAlign: "center",
                  }}
                >
                  {onSelectAll && (() => {
                    const allRowIds = data.map((r) => r?._rowId).filter(Boolean);
                    const selectedCount = allRowIds.filter((id) => selectedRowIds.includes(id)).length;
                    const allSelected = allRowIds.length > 0 && selectedCount === allRowIds.length;
                    const someSelected = selectedCount > 0 && !allSelected;
                    return (
                      <input
                        type="checkbox"
                        title={allSelected ? "Deselect all" : "Select all"}
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected;
                        }}
                        onChange={() => onSelectAll(!allSelected)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    );
                  })()}
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.name}
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    padding: "14px 10px",
                    background: headerBg,
                    color: headerText,
                    fontWeight: 700,
                    borderBottom: `3px solid ${highlight}`,
                    minWidth: col.width || "8rem", // Use column width when available - responsive rem units
                    width: col.width || "8rem", // Set actual width for proper sizing
                    textAlign: "left",
                    letterSpacing: "0.03em",
                    fontSize: "1rem",
                    boxShadow: isLight
                      ? "0 2px 4px rgba(0,0,0,0.04)"
                      : "0 2px 4px rgba(0,0,0,0.04)",
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentItems.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{
                    textAlign: "center",
                    color: textColor,
                    padding: "24px",
                  }}
                >
                  No data available
                </td>
              </tr>
            ) : (
              currentItems.map((row, rowIdx) => {
                const absIdx = rowIdx + indexOfFirstItem;
                const rowId = row?._rowId;
                const isSelectedById =
                  Array.isArray(selectedRowIds) &&
                  rowId !== undefined &&
                  selectedRowIds.includes(rowId);
                const isSelected = isSelectedById || absIdx === selectedRowIdx;
                return (
                  <tr
                    key={absIdx}
                    className={isSelected ? "selected-row" : ""}
                    style={{
                      background:
                        isSelected
                          ? "#ff4e3a"
                          : (absIdx % 2 === 0 ? rowEven : rowOdd),
                      transition: "background 0.2s",
                      cursor: "pointer",
                      color: isSelected ? "#fff" : textColor,
                    }}
                    onClick={() => onRowSelect(indexOfFirstItem + rowIdx, rowId)}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.background = isSelected
                        ? "#2e5cff"
                        : isLight
                        ? "#f0f0f0"
                        : headerBg)
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.background = isSelected
                        ? "#2e5cff"
                        : absIdx % 2 === 0
                        ? rowEven
                        : rowOdd)
                    }
                  >
                    {enableCheckboxSelection && (
                      <td
                        style={{
                          padding: "12px 10px",
                          width: "3rem",
                          textAlign: "center",
                          borderBottom: `1px solid ${borderColor}`,
                          borderRight: `1px solid ${borderColor}`,
                          background: isSelected ? "#ff4e3a" : "inherit",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={
                            !!(
                              Array.isArray(selectedRowIds) &&
                              rowId !== undefined &&
                              selectedRowIds.includes(rowId)
                            )
                          }
                          onChange={(e) => {
                            e.stopPropagation();
                            onRowSelect(indexOfFirstItem + rowIdx, rowId);
                          }}
                        />
                      </td>
                    )}
                    {columns.map((col) => {
                      const value = row[col.name] || "";
                      const isError = isErrorValue(value);
                      const isEditing =
                        activeEditingCell.rowIdx === absIdx &&
                        activeEditingCell.colName === col.name;
                      const fieldType = getFieldType(col.name);
                      
                      return (
                        <td
                          key={col.name}
                          style={{
                            padding: "12px 10px",
                            minWidth: col.width || "8rem", // Use column width when available - responsive rem units
                            width: col.width || "8rem", // Set actual width for proper sizing
                            color: isSelected
                              ? "#fff"
                              : isError
                              ? highlight
                              : textColor,
                            fontWeight: isError ? 700 : 400,
                            borderBottom: `1px solid ${borderColor}`,
                            fontSize: "0.98rem",
                            whiteSpace: "pre-line",
                            borderRight: `1px solid ${borderColor}`,
                            verticalAlign: "top",
                            background: isError && !isSelected ? highlightBg : isSelected ? "#ff4e3a" : "inherit",
                            borderRadius: isError ? "6px" : 0,
                            boxShadow: isError
                              ? `0 0 4px 0 ${highlightBg}`
                              : "none",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditStart(absIdx, col.name, value);
                          }}
                        >
                          {isEditing ? (
                            renderInputField(rowIdx, col.name, value, 
                              typeof fieldType === 'object' ? fieldType : fieldType)
                          ) : (
                            value
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination Bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 16,
          marginTop: 8,
          padding: "8px 16px",
          fontSize: 14,
          color: textColor,
        }}
      >
        <span style={{ marginRight: 8 }}>Rows per page:</span>
        <select
          value={rowsPerPage}
          onChange={(e) => {
            const newRowsPerPage = Number(e.target.value);
            setRowsPerPage(newRowsPerPage);
            // Recalculate current page to ensure it's valid
            const newTotalPages = Math.ceil(data.length / newRowsPerPage);
            setCurrentPage(Math.min(currentPage, newTotalPages || 1));
          }}
          style={{
            borderRadius: 8,
            padding: "4px 8px",
            border: `1px solid ${borderColor}`,
            color: textColor,
            background: tableBg,
            outline: "none",
            marginRight: 16,
          }}
        >
          {itemsPerPageOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        <span style={{ marginRight: 8 }}>
          {data.length === 0 ? 0 : indexOfFirstItem + 1} -{" "}
          {Math.min(indexOfLastItem, data.length)} of {data.length}
          {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
        </span>
        <button
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          style={{
            border: "none",
            background: "none",
            color: currentPage === 1 ? "#aaa" : highlight,
            fontSize: 18,
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
            marginRight: 8,
          }}
        >
          &#8592;
        </button>
        <span style={{ fontWeight: 600 }}>{currentPage}</span>
        <button
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages || totalPages === 0}
          style={{
            border: "none",
            background: "none",
            color:
              currentPage === totalPages || totalPages === 0
                ? "#aaa"
                : highlight,
            fontSize: 18,
            cursor:
              currentPage === totalPages || totalPages === 0
                ? "not-allowed"
                : "pointer",
            marginLeft: 8,
          }}
        >
          &#8594;
        </button>
      </div>
    </>
  );
}

export default ThemedTable;
